/**
 * SpotIllustration -- the shared frame for the CRM's custom empty-state spot
 * illustrations (#2580, Tier 5 "moments & identity" of the world-class UI/UX
 * epic #2575).
 *
 * The design survey found "zero custom illustrations -- every empty state is a
 * Lucide icon + text". This is the shared wrapper every spot illustration in
 * this folder renders through, so they share ONE contract:
 *
 *   - a fixed ~160x120 viewBox (line-art, generous whitespace);
 *   - line-art driven by TOKENS, not hex: the muted structural lines use
 *     `currentColor` (the SVG root sets `color: var(--color-text-muted)`), so a
 *     child stroke of `currentColor` adapts to light/dark FOR FREE, and the
 *     single brand accent uses `var(--color-accent)`;
 *   - `aria-hidden` -- the empty-state TITLE carries the meaning, never the art
 *     (WCAG: decorative image);
 *   - agency-neutral -- no logos, skylines, or city references.
 *
 * These are DECORATIVE spot illustrations, intentionally simple and quiet
 * (taste gate: "quiet-Apple, not gamified"). They render in place of the muted
 * Lucide icon in `EmptyState` via its `illustration` slot -- the icon remains the
 * default so no existing page churns.
 *
 * Zero new npm dependency: hand-authored inline SVG.
 */
import type { ReactNode } from "react";

export interface SpotIllustrationProps {
  /** The SVG line-art children (paths/shapes), authored against a 160x120 box. */
  children: ReactNode;
  /** Extra classes on the <svg> (sizing is the default `h-28 w-auto`). */
  className?: string;
}

/**
 * Renders the shared 160x120 line-art frame. The root `text-[var(--color-text-muted)]`
 * makes `currentColor` resolve to the muted line colour (theme-adaptive), so a
 * child can stroke with `currentColor` for the structural lines and
 * `var(--color-accent)` for the single brand highlight.
 */
export function SpotIllustration({
  children,
  className = "",
}: SpotIllustrationProps) {
  return (
    <svg
      viewBox="0 0 160 120"
      role="img"
      aria-hidden="true"
      focusable="false"
      className={
        "h-28 w-auto text-[var(--color-text-muted)]" +
        (className ? ` ${className}` : "")
      }
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  );
}

export default SpotIllustration;
